import * as React from "react";

export interface ImageLoaderProps {
  /** Full-resolution image URL — fades in when loaded. */
  src: string;
  /** Low-resolution placeholder URL — shown blurred while src loads. */
  placeholderSrc?: string;
  alt?: string;
  objectFit?: "cover" | "contain";
  /** Applied to the outer container div. */
  style?: React.CSSProperties;
  className?: string;
  /** Fired when the full-resolution `src` fails to load. */
  onError?: () => void;
}

/**
 * Progressive blur-up image loader. Shows `placeholderSrc` (blurred) immediately,
 * then crossfades to `src` when it finishes loading.
 *
 * The container is `position: relative; width: 100%; height: 100%` — it inherits
 * its dimensions from the parent, so the parent must have an explicit size.
 */
export function ImageLoader({
  src,
  placeholderSrc,
  alt = "",
  objectFit = "cover",
  style,
  className,
  onError,
}: ImageLoaderProps) {
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    setLoaded(false);
  }, [src]);

  const fill: React.CSSProperties = {
    position: "absolute", inset: 0,
    width: "100%", height: "100%",
    objectFit, display: "block",
  };

  function handleLoad() {
    // Double RAF ensures the browser paints the initial unloaded state before
    // the transition starts — otherwise cached images skip the animation entirely.
    requestAnimationFrame(() => requestAnimationFrame(() => setLoaded(true)));
  }

  return (
    <div
      style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden", ...style }}
      className={className}
    >
      {placeholderSrc && (
        <img
          src={placeholderSrc}
          alt=""
          style={{ ...fill, filter: "blur(12px)", transform: "scale(1.05)" }}
        />
      )}
      <img
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={onError}
        style={{
          ...fill,
          opacity: loaded ? 1 : 0,
          filter: loaded ? "blur(0px)" : "blur(6px)",
          transform: loaded ? "scale(1)" : "scale(1.02)",
          transition: loaded
            ? "opacity 0.5s ease, filter 0.5s ease, transform 0.5s ease"
            : "none",
        }}
      />
    </div>
  );
}
